/**
 * StudentLiveLayers — ce que l'écran du live pose par-dessus sa page : le chat et la conversation de
 * Sam (les deux calques que la page place, le chat devant), les bandeaux de l'écran (`useNotices`, que
 * la page pose dans sa file, au-dessus de sa barre), puis les feuilles d'à côté : « Un problème ? »,
 * « Mes activités », « Tu veux parler à Sam ? », le tuteur absent à dix minutes, la note en partant,
 * le message de clôture, « Appeler / Envoyer un SMS » pour un numéro touché dans le chat.
 */
import React, { useCallback, useEffect, useRef, useState } from "react"

import { ConfirmDialog, MediaDisplay } from "../../../../../../design-system"
import {
    LiveActivitiesSheet,
    LiveChat,
    LiveClosedDialog,
    LiveTutorAbsentDialog,
    SamSheet,
    LiveRatingModal,
    LiveAutoModeSheet,
    type UILiveScreenNotice,
} from "../../../../../../design-system/student"
import { type ApiCheckpointConversation } from "../../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import { liveMicDroppedCopy } from "../../../../view-models/student/Live/studentLiveViewModel"
import { ConversationPhoneSheet } from "../Messaging/ConversationPhoneSheet"
import { ConversationVoiceRecorder } from "../Messaging/ConversationVoiceRecorder"
import { LIVE_EVENTS, trackLive } from "./liveTracking"
import { LiveVideo } from "./LiveVideo"
import { StudentLiveCheckpoint } from "./StudentLiveCheckpoint"
import { StudentLiveDevices } from "./StudentLiveDevices"
import { StudentLiveHelp } from "./StudentLiveHelp"
import { type StudentLiveScreenModel } from "./useStudentLiveScreen"

/**
 * Le panneau de Sam est posé : la conversation d'un checkpoint, ou son ouverture (Sam touché, sa
 * première phrase en route). Le quiz de prérequis a sa feuille, posée par l'activité : la main levée
 * attend qu'il se ferme.
 */
function samPanelShown(m: StudentLiveScreenModel): boolean {
    if (m.activityView.quiz.blocking) return false
    return m.live.checkpoint != null || m.live.checkpointOpening === "opening" || m.live.checkpointOpening === "failed"
}

/** Le chat ouvert. La page le pose après le panneau de Sam : ouvert pendant la conversation, il passe devant. */
function chat(m: StudentLiveScreenModel): React.ReactNode {
    const { chat: liveChat } = m.live
    if (!liveChat.open) return undefined
    return (
        <SamSheet onClose={() => liveChat.setOpen(false)} accessibilityLabel="Chat du live">
            <LiveChat
                tutor={m.tutorUI}
                days={m.chatDays}
                channel={liveChat.channel}
                channels={m.chatChannels}
                onChangeChannel={liveChat.setChannel}
                loading={liveChat.loading}
                readOnly={liveChat.readOnly}
                draft={liveChat.draft}
                onChangeDraft={liveChat.setDraft}
                onSend={liveChat.sendMessage}
                sending={liveChat.sending}
                attachSources={m.chatAttachments.attachSources}
                onAttach={m.chatAttachments.onAttach}
                attaching={liveChat.attaching}
                onOpenAttachment={m.chatAttachments.onOpenAttachment}
                voicePlayback={m.chatAttachments.voicePlayback}
                onToggleVoice={m.chatAttachments.onToggleVoice}
                onSeekVoice={m.chatAttachments.onSeekVoice}
                onCycleVoiceRate={m.chatAttachments.onCycleVoiceRate}
                onPressLink={m.chatAttachments.onPressLink}
                onRetryMessage={(message) => liveChat.retry(message.id)}
                typingLabel={m.chatTyping}
                hasEarlier={liveChat.hasEarlier}
                loadingEarlier={liveChat.loadingEarlier}
                onLoadEarlier={liveChat.loadEarlier}
                onClose={() => liveChat.setOpen(false)}
            />
        </SamSheet>
    )
}

/** Le panneau de Sam : sa conversation, ou son ouverture. */
function checkpoint(m: StudentLiveScreenModel): React.ReactNode {
    if (!samPanelShown(m)) return undefined
    return (
        <StudentLiveCheckpoint
            live={m.live}
            conversation={m.live.checkpoint as ApiCheckpointConversation | null}
            activityId={m.activityId}
            onSuggestActivity={m.live.actions.selectActivity}
        />
    )
}

/** Sam touché demande d'abord (Wajih, 25/10) : une question fermée, oui ou non. */
const SAM_CONFIRM = { title: "Tu veux parler à Sam ?", yes: "Oui", no: "Non" }

/** « Activité soumise » reste 6 s, comme l'ancien (`SnackBarM`, `duration: 6000`). */
const VALIDATED_NOTICE_MS = 6000
/** La correction prête porte un geste : le temps de le lire et de toucher « Voir » (proposé). */
const CORRECTION_NOTICE_MS = 8000

/** Un bandeau fermé le reste tant que dure ce qu'il dit (`during`) : il revient à la fois suivante. */
function useDismissedWhile(during: boolean): [boolean, () => void] {
    const [dismissed, setDismissed] = useState(false)
    useEffect(() => {
        if (!during && dismissed) setDismissed(false)
    }, [during, dismissed])
    const dismiss = useCallback(() => setDismissed(true), [])
    return [dismissed, dismiss]
}

/**
 * Les bandeaux de l'écran : le micro ouvert qui ne capte aucun son (« Voir » ouvre « Un problème ? »),
 * l'activité rendue par le tuteur, la correction arrivée pendant que l'élève était ailleurs (« Voir »
 * l'ouvre), le signalement parti, Sam touché en Dialogue, la réponse du serveur à un remplacement refusé. La page les pose dans la file des siens (`notices`) : un à la
 * fois, dans l'ordre d'arrivée, au-dessus de la barre du live.
 */
function useNotices(m: StudentLiveScreenModel): UILiveScreenNotice[] {
    const { live, sheets } = m
    // Les bandeaux relancent leur minuteur quand leur `onDismiss` change : des fonctions stables.
    const latest = useRef(m)
    latest.current = m
    const dismissValidated = useCallback(() => latest.current.live.actions.dismissValidated(), [])
    const dismissReported = useCallback(() => latest.current.sheets.setReported(false), [])
    const dismissNotice = useCallback(() => latest.current.live.waiting.dismissNotice(), [])
    const dismissSamAway = useCallback(() => latest.current.sheets.dismissSamAway(), [])
    const correction = m.correctionReady
    const correctionId = correction?.id ?? null
    const dismissCorrection = useCallback(() => {
        if (correctionId != null) latest.current.sheets.dismissCorrection(correctionId)
    }, [correctionId])
    // Le micro muet se dit une fois par micro ouvert : refermé, il ne revient qu'après un micro coupé puis
    // rouvert (et « Un problème ? » le garde marqué entre-temps).
    const [silenceDismissed, dismissSilence] = useDismissedWhile(live.micOn)
    // Le micro tombé se dit jusqu'à ce qu'il se rouvre : fermé, il revient au prochain micro tombé.
    const [dropDismissed, dismissDrop] = useDismissedWhile(live.micDropped)
    const reopenMic = useCallback(() => latest.current.live.actions.toggleMic(), [])
    const dropped = liveMicDroppedCopy(m.tutorUI.firstname)
    const openMicHelp = useCallback(() => latest.current.sheets.openHelp(), [])

    return [
        {
            // Le micro coupé sans geste de l'élève (la piste perdue) : le masteur ne l'entend plus.
            key: "micDropped",
            visible: live.micDropped && !dropDismissed,
            message: dropped.message,
            tone: "danger",
            iconName: "microphone-slash",
            autoHideMs: 0,
            action: { label: dropped.action, onPress: reopenMic },
            onDismiss: dismissDrop,
        },
        {
            // La barre montre le micro ouvert, et le masteur n'entend rien : l'élève doit le savoir.
            key: "micSilent",
            visible: live.micSilent && !silenceDismissed,
            message: "On ne t'entend pas : ton micro ne capte aucun son.",
            tone: "danger",
            iconName: "microphone-slash",
            autoHideMs: 0,
            action: { label: "Voir", onPress: openMicHelp },
            onDismiss: dismissSilence,
        },
        {
            key: "validated",
            visible: live.validatedNotice != null,
            message: live.validatedNotice ?? "",
            autoHideMs: VALIDATED_NOTICE_MS,
            onDismiss: dismissValidated,
        },
        {
            key: "correction",
            visible: correction != null,
            message: correction != null ? `Ta correction de « ${correction.title} » est prête` : "",
            tone: "info",
            iconName: "clipboard-check",
            autoHideMs: CORRECTION_NOTICE_MS,
            action:
                correctionId != null
                    ? { label: "Voir", onPress: () => latest.current.sheets.openCorrection(correctionId) }
                    : undefined,
            onDismiss: dismissCorrection,
        },
        {
            key: "reported",
            visible: sheets.reported,
            message: "Signalement envoyé, merci !",
            onDismiss: dismissReported,
        },
        {
            // Sam touché en Dialogue, ou sa conversation interrompue par le Dialogue : une phrase courte, qu'un
            // jeune élève comprend d'un coup (Wajih, daily du 21/10).
            key: "samAway",
            visible: sheets.samAway,
            message: "Sam fait une pause : ton masteur te parle.",
            tone: "info",
            onDismiss: dismissSamAway,
        },
        {
            key: "waiting",
            visible: live.waiting.notice != null,
            message: live.waiting.notice ?? "",
            tone: "info",
            onDismiss: dismissNotice,
        },
    ]
}

function Layers({ model: m }: { model: StudentLiveScreenModel }) {
    const { live, sheets } = m
    const { waiting, leave } = live
    const dialog = waiting.dialog
    const tutorFirstname = m.tutorUI.firstname || "ton masteur"

    return (
        <>
            <StudentLiveHelp
                visible={sheets.helpOpen}
                initialProblem={sheets.helpProblem}
                context={sheets.helpContext}
                liveSessionId={live.liveSessionId}
                onClose={sheets.closeHelp}
                onRaiseHand={live.actions.toggleHand}
                onOpenChat={() => live.chat.setOpen(true)}
                onReported={() => sheets.setReported(true)}
                telemetry={live.telemetry}
            />
            <LiveActivitiesSheet
                visible={sheets.activitiesOpen}
                onClose={() => sheets.setActivitiesOpen(false)}
                activities={m.todos}
                onSelect={(activity) => sheets.selectTodo(Number(activity.id))}
                doneActivities={m.doneActivities}
                onSelectDone={(activity) => sheets.openDone(activity.id)}
                onRefresh={sheets.refreshTodos}
                refreshing={sheets.refreshingTodos}
                onSeeAllDone={sheets.seeAllDone}
            />
            <ConfirmDialog
                visible={sheets.samConfirm}
                title={SAM_CONFIRM.title}
                confirmLabel={SAM_CONFIRM.yes}
                cancelLabel={SAM_CONFIRM.no}
                danger={false}
                onConfirm={sheets.confirmSam}
                onCancel={sheets.cancelSam}
            />
            {m.devices != null && m.devicesState.open && (
                <StudentLiveDevices
                    devices={m.devices}
                    tab={m.devicesState.tab}
                    onChangeTab={(tab) => m.devicesDispatch({ type: "TAB", tab })}
                    onSelected={(tab) => m.devicesDispatch({ type: "SELECTED", tab })}
                    onClose={() => m.devicesDispatch({ type: "CLOSE" })}
                    micMeasurable={m.micAllowed !== false && !live.micGate.held}
                    focused={!live.dialogue}
                    cameraPreview={m.tracks.me != null ? <LiveVideo track={m.tracks.me} source="me" /> : undefined}
                    onOpenHelp={sheets.openHelp}
                    noise={m.noiseFilter}
                />
            )}
            {m.autoModeSheet != null && (
                <LiveAutoModeSheet
                    {...m.autoModeSheet}
                    onClose={m.autoMode.close}
                    onEvaluate={m.autoMode.evaluate}
                    onChoose={(proposal) => {
                        const state = m.autoMode.state
                        const chosen =
                            state.phase === "choosing"
                                ? state.proposals.find((item) => String(item.material.id) === proposal.id)
                                : undefined
                        if (chosen != null) m.autoMode.choose(chosen.material)
                    }}
                    onReplay={
                        m.autoMode.state.phase === "choosing" && m.autoMode.state.source.material?.id != null
                            ? m.autoMode.replay
                            : null
                    }
                    onRaiseHand={() => {
                        if (!live.state.handRaised.value) live.actions.toggleHand()
                        m.autoMode.close()
                    }}
                />
            )}
            {dialog != null && (
                <LiveTutorAbsentDialog
                    visible
                    step={dialog.step}
                    replacement={m.replacement}
                    creditReason={dialog.creditReason}
                    onJoin={waiting.join}
                    onDecline={waiting.decline}
                    onConfirmJoin={waiting.confirmJoin}
                    onConfirmLater={waiting.confirmLater}
                    onBack={waiting.back}
                    joining={dialog.joining}
                />
            )}
            <LiveRatingModal
                visible={leave.rating.open}
                tutorFirstname={tutorFirstname}
                rating={leave.rating.rating}
                onRate={leave.rate}
                subtitle={leave.rating.subtitle}
                comments={leave.rating.comments}
                selectedComments={leave.rating.selected}
                onToggleComment={leave.toggleComment}
                onDismiss={() => {
                    trackLive(LIVE_EVENTS.ratingClosed)
                    leave.dismissRating()
                }}
                onSubmit={leave.submitRating}
                submitting={leave.rating.submitting}
            />
            {/* La note ouverte passe devant ; la sortie partie, le message s'efface : le quiz ou le rapport,
                poussés au geste, sont dessous. Son bouton demande toujours la note, comme le rapport : la séance
                close n'est plus la prochaine occurrence, la règle du quart d'heure ne s'y lit plus. */}
            <LiveClosedDialog
                visible={m.closedDialog != null && !leave.rating.open && !leave.leaving}
                quizMinutes={m.closedDialog?.quizMinutes}
                onExit={leave.openRating}
            />
            {m.chatAttachments.viewer?.kind === "image" && (
                <MediaDisplay
                    kind="image"
                    visible
                    images={m.chatAttachments.viewer.images}
                    initialIndex={m.chatAttachments.viewer.index}
                    onClose={m.chatAttachments.closeViewer}
                />
            )}
            {m.chatAttachments.viewer?.kind === "pdf" && (
                <MediaDisplay
                    kind="pdf"
                    visible
                    uri={m.chatAttachments.viewer.uri}
                    onClose={m.chatAttachments.closeViewer}
                />
            )}
            {m.chatAttachments.recorder != null && <ConversationVoiceRecorder {...m.chatAttachments.recorder} />}
            <ConversationPhoneSheet
                phone={m.chatAttachments.phone}
                onChoose={m.chatAttachments.onChoosePhone}
                onClose={m.chatAttachments.onClosePhone}
            />
        </>
    )
}

export const StudentLiveLayers = Object.assign(Layers, { chat, checkpoint, useNotices })
